import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import RebalancingDisclaimer from '../../components/power-rebalancing/RebalancingDisclaimer'
import Factbox from '../../components/regular-to-direct-plan/Factbox'
import { RegularToDirectPlanFAQs } from '../../components/regular-to-direct-plan/RegularToDirectPlanFAQs'
import Heading from '../../components/regular-to-direct-plan/Heading'
import HelperText from '../../components/regular-to-direct-plan/HelperText'
import { EliteOfferBanner } from '../../components/elite/landing-page/versions/landing-page-head/EliteOfferBanner'
import RegularPlanList from '../../components/regular-to-direct-plan/RegularPlanList'
import TopLogo from '../../components/regular-to-direct-plan/TopLogo'
import Button from '../../components/ui-components/Button'
import { getTopPadding } from '../../utils/sessionStorage'
import { goBack, lightImpactVibrate, openEliteLandingPage, trackEvent, updateAppStatusBar, disableHardwareBack, onReady } from '../../utils/appBridge'
import { throttle } from '../../utils/utils'
import { useNavigate, useSearchParams } from 'react-router-dom'
import BackButton from '../../components/ui-components/BackButton'
import { fetchRegularToDirectSummary } from '../../repository/regular-to-direct-plan'
import { EventNames } from '../../types/misc.types'
import { useAppMetaContent } from '../../state-management/providers/AppMetaContentProvider'

export default function RegularToDirectPlanPage() {
  const [showSmallHeader, setShowSmallHeader] = useState(false)
  const [backgroundColor, setBackgroundColor] = useState('#FFEEEC') // Default to header background
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const listOfFundsRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const { getEliteMembersDisplay } = useAppMetaContent()

  const {
    data: summaryData,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['regularToDirectSummary'],
    queryFn: fetchRegularToDirectSummary,
    retry: 1,
    refetchOnWindowFocus: false
  })

  const handleBack = () => {
    lightImpactVibrate()
    if (from === 'APP') goBack()
    else goBack()
  }

  const headerHeight = `calc(${getTopPadding()}px + 54px)`

  const throttledScroll = throttle((scrollPosition: number) => {
    setShowSmallHeader(scrollPosition >= 70)
  }, 0)

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollPosition = e.currentTarget.scrollTop || 0
    throttledScroll(scrollPosition)
  }

  useEffect(() => {
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        screenType: 'REGULAR_TO_DIRECT_PLAN_PAGE'
      }
    })
    updateAppStatusBar('FFFFFF', 'dark')
    disableHardwareBack()
  }, [])

  useEffect(() => {
    if (!isLoading && !isError && summaryData) {
      onReady()
    }
  }, [isLoading, isError, summaryData])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const headerElement = headerRef.current
    const listOfFundsElement = listOfFundsRef.current

    if (!scrollContainer || !headerElement || !listOfFundsElement) return

    const getBackgroundColor = (element: HTMLElement | null): string => {
      if (!element) return '#FFEEEC'
      const computedStyle = window.getComputedStyle(element)
      return computedStyle.backgroundColor || '#FFEEEC'
    }

    const updateBackgroundColor = () => {
      const containerRect = scrollContainer.getBoundingClientRect()
      const headerRect = headerElement.getBoundingClientRect()
      const listOfFundsRect = listOfFundsElement.getBoundingClientRect()

      // Check which element is more visible in the viewport
      const headerVisible = headerRect.bottom > containerRect.top && headerRect.top < containerRect.bottom
      const listOfFundsVisible = listOfFundsRect.bottom > containerRect.top && listOfFundsRect.top < containerRect.bottom

      if (listOfFundsVisible && listOfFundsRect.top <= containerRect.top) {
        // If listOfFunds is visible and near the top, use its background
        const bgColor = getBackgroundColor(listOfFundsElement)
        setBackgroundColor(bgColor)
      } else if (headerVisible) {
        // Otherwise use header background
        const bgColor = getBackgroundColor(headerElement)
        setBackgroundColor(bgColor)
      } else if (listOfFundsVisible) {
        // Fallback to listOfFunds background
        const bgColor = getBackgroundColor(listOfFundsElement)
        setBackgroundColor(bgColor)
      }
    }

    // Initial check
    updateBackgroundColor()

    // Listen to scroll events
    scrollContainer.addEventListener('scroll', updateBackgroundColor)
    window.addEventListener('resize', updateBackgroundColor)

    return () => {
      scrollContainer.removeEventListener('scroll', updateBackgroundColor)
      window.removeEventListener('resize', updateBackgroundColor)
    }
  }, [])

  return (
    <div className='h-[100vh] w-full bg-[#FFEEEC] flex flex-col overflow-hidden'>
      <div
        style={{ height: headerHeight, backgroundColor: backgroundColor }}
        key='small'
        id='fullHeaderSmall'
        className='w-full flex z-[99] flex-shrink-0 transition-colors duration-200'
      >
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 16px)`, backgroundColor: backgroundColor }}
          className={`flex items-center relative w-full pb-5 pr-5 pl-[19px] h-full transition-colors duration-300 border-b ${showSmallHeader ? 'border-[#EBF2F2]' : 'border-transparent'}`}
        >
          <div className='flex absolute z-1'>
            <BackButton size={24} onClick={handleBack} />
          </div>
          <div className='w-full flex justify-start ml-10 items-start'>
            {showSmallHeader && (
              <div className='max-w-[70%] animate-fade-150 truncate font-[500] mr-4'>
                Regular to Direct Plan
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        style={{
          height: `calc(100vh - ${headerHeight})`
        }}
        onScroll={handleScroll}
        className='w-full flex flex-col flex-1 overflow-y-scroll no-scrollbar overflow-x-hidden overscroll-none'
      >
        <div ref={headerRef} id="header" className='px-5 pb-8 bg-[#FFEEEC]'>
          <TopLogo />
          <Heading />
          <Factbox
            regularFundCount={summaryData?.regularFundCount || 0}
            additionalCommissionsPaid={summaryData?.additionalCommissions || 0}
          />
          <HelperText />
          <EliteOfferBanner
            specialOffering={summaryData?.userSubscriptionDetails?.specialOffering ?? false}
            signupOffer={summaryData?.userSubscriptionDetails?.signupOffer ?? false}
            offerStartTime={summaryData?.userSubscriptionDetails?.offerStartTime}
            offerExpiryTime={summaryData?.userSubscriptionDetails?.offerExpiryTime}
            membershipFees={summaryData?.userSubscriptionDetails?.membershipFees?.amount ?? 0}
            offerType={summaryData?.userSubscriptionDetails?.offerType}
            notElitePage={true}
            screenType='REGULAR_TO_DIRECT_PLAN_PAGE'
          />
          <Button
            buttonClassName='bg-black h-[52px] px-6 py-4 text-[16px] font-medium leading-[1.2] bg-[#992E00] text-white mt-7'
            isFullWidth={true}
            onClick={() => {
              trackEvent({
                eventName: EventNames.ButtonTap,
                payload: {
                  buttonLabel: 'Unlock plan with Elite',
                  eventId: 'UNLOCK_ELITE_BUTTON',
                  screenType: 'REGULAR_TO_DIRECT_PLAN_PAGE'
                }
              })
              openEliteLandingPage()
            }}
          >
            Unlock plan with Elite
          </Button>
          <div className='flex items-center justify-center text-13 text-status-text-out-of-form opacity-60 mt-3 leading-[1]'>
            Trusted by {getEliteMembersDisplay('short')} Elite members
          </div>
        </div>
        <div ref={listOfFundsRef} id="listOfFunds" className='bg-white'>
          <RegularPlanList summaryData={summaryData} isLoading={isLoading} isError={isError} />
        </div>
        <RegularToDirectPlanFAQs screenType='REGULAR_TO_DIRECT_PLAN_PAGE' />
        <RebalancingDisclaimer />
      </div>
    </div>
  )
}
